// src/features/calendar/settings/CalendarsSettings.tsx
// Manage calendars: create/edit/delete (via CalendarFormDialog, the shared
// form-kit dialog), and share them with colleagues (members with roles).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendar, useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import type {
  Calendar,
  CalendarMember,
  CalendarMemberRole,
} from '@core/types/calendar';
import type { PaginatedRequest } from '@core/types/pagination';
import { ModalShell } from '../ModalShell';
import { errMessage, makeEntity } from '../time';
import { CalendarFormDialog } from './CalendarFormDialog';

const MEMBER_ROLES: CalendarMemberRole[] = ['CALENDAR_MEMBER_ROLE_FREE_BUSY', 'CALENDAR_MEMBER_ROLE_VIEWER', 'CALENDAR_MEMBER_ROLE_EDITOR', 'CALENDAR_MEMBER_ROLE_ADMIN'];

const typeKey = (v: string) => v.replace('CALENDAR_TYPE_', '').toLowerCase();
const visKey = (v: string) => v.replace('CALENDAR_VISIBILITY_', '').toLowerCase();
const roleKey = (v: string) => v.replace('CALENDAR_MEMBER_ROLE_', '').toLowerCase();

export const CalendarsSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendar();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Calendar | null>(null);
  const [deleting, setDeleting] = useState<Calendar | null>(null);

  // members
  const [membersFor, setMembersFor] = useState<Calendar | null>(null);
  const [members, setMembers] = useState<CalendarMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addRole, setAddRole] = useState<CalendarMemberRole>('CALENDAR_MEMBER_ROLE_VIEWER');
  const [membersError, setMembersError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    store
      .ListCalendars({
        pagination: { cursor: '', limit: 100, direction: 'forward' },
      } as PaginatedRequest)
      .catch((e: unknown) => setError(errMessage(e, t('calendar.loadError'))));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  useEffect(() => { load(); }, []);

  const openDialog = async (listed: Calendar | null) => {
    let cal = listed;
    if (listed?.entity?.id) {
      // Load the full calendar — the list payload may be trimmed.
      try {
        cal = await store.GetCalendar(listed.entity.id);
      } catch {
        cal = listed;
      }
    }
    setEditing(cal);
    setDialogOpen(true);
  };

  const openMembers = async (cal: Calendar) => {
    setMembersFor(cal);
    setMembersLoading(true);
    setMembersError(null);
    try {
      const res = await store.ListCalendarMembers(cal.entity?.id ?? '');
      setMembers(res.members?.members ?? []);
    } catch (e) {
      setMembersError(errMessage(e, t('calendar.members.loadError')));
    } finally {
      setMembersLoading(false);
    }
  };

  const memberAction = async (fn: () => Promise<unknown>) => {
    setMembersError(null);
    try {
      await fn();
      if (membersFor) await openMembers(membersFor);
    } catch (e) {
      setMembersError(errMessage(e, t('calendar.members.actionError')));
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('calendar.calendarsSettings.hint')}</p>
        <Button size="sm" onClick={() => openDialog(null)}>
          {t('calendar.calendarsSettings.newCalendar')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {store.isLoading && store.items.length === 0 && (
        <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />
      )}

      {!store.isLoading && store.items.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('calendar.noCalendars')}</p>
          <Button size="sm" className="mt-3" onClick={() => openDialog(null)}>
            {t('calendar.calendarsSettings.newCalendar')}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((cal) => (
          <div key={cal.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-sm leading-none"
                style={{ backgroundColor: cal.color ? `${cal.color}26` : 'hsl(var(--muted))' }}
                aria-hidden="true"
              >
                {cal.icon || (
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: cal.color || 'hsl(var(--primary))' }} />
                )}
              </span>
              <span className="truncate font-medium text-foreground">{cal.name}</span>
              <Badge variant="default">{t(`calendar.type.${typeKey(cal.type ?? 'CALENDAR_TYPE_PERSONAL')}`)}</Badge>
              <Badge variant="secondary">{t(`calendar.visibilityLevel.${visKey(cal.visibility ?? 'CALENDAR_VISIBILITY_PRIVATE')}`)}</Badge>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => openMembers(cal)}>
                {t('calendar.members.title')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openDialog(cal)}>
                {t('common.edit')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(cal)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Create / edit calendar — shared form-kit dialog (docs/ux/14-forms.md) */}
      <CalendarFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        calendar={editing}
        onSaved={load}
      />

      {/* Members */}
      <ModalShell
        open={!!membersFor}
        onClose={() => setMembersFor(null)}
        title={membersFor ? `${t('calendar.members.title')} — ${membersFor.name}` : t('calendar.members.title')}
      >
        <div className="space-y-3">
          {membersError && <Alert variant="error">{membersError}</Alert>}
          {membersLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!membersLoading && members.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('calendar.members.empty')}</p>
          )}
          <ul className="space-y-1.5">
            {members.map((m) => {
              const memberId = m.member?.entity?.id ?? m.entity?.id ?? '';
              return (
                <li key={memberId} className="flex items-center justify-between gap-2 text-sm text-foreground">
                  <span className="truncate">{m.member?.display_name || memberId}</span>
                  <span className="flex items-center gap-1.5">
                    <Select
                      aria-label={t('calendar.members.role')}
                      value={m.role ?? 'CALENDAR_MEMBER_ROLE_VIEWER'}
                      onChange={(e) =>
                        memberAction(() =>
                          store.UpdateCalendarMemberRole(membersFor?.entity?.id ?? '', memberId, {
                            calendar: makeEntity(membersFor?.entity?.id ?? '', 'calendar', scope),
                            member: makeEntity(memberId, 'profile', scope),
                            role: e.target.value as CalendarMemberRole,
                          }),
                        )
                      }
                      fullWidth={false}
                      className="w-36"
                      options={MEMBER_ROLES.map((r) => ({ value: r, label: t(`calendar.memberRole.${roleKey(r)}`) }))}
                    />
                    <button
                      type="button"
                      aria-label={t('calendar.members.remove')}
                      onClick={() =>
                        memberAction(() =>
                          store.RemoveCalendarMember(membersFor?.entity?.id ?? '', memberId),
                        )
                      }
                      className="rounded-md p-1 text-muted-foreground hover:text-destructive"
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="space-y-1.5 border-t border-border pt-3">
            <Select
              label={t('calendar.members.addWithRole')}
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as CalendarMemberRole)}
              options={MEMBER_ROLES.map((r) => ({ value: r, label: t(`calendar.memberRole.${roleKey(r)}`) }))}
            />
            <UserPicker
              value=""
              placeholder={t('calendar.members.add')}
              onChange={(userId, displayName) => {
                if (!userId) return;
                memberAction(() =>
                  store.AddCalendarMember(membersFor?.entity?.id ?? '', {
                    calendar: makeEntity(membersFor?.entity?.id ?? '', 'calendar', scope),
                    member: { entity: makeEntity(userId, 'profile', scope), display_name: displayName, avatar: '' },
                    role: addRole,
                  }),
                );
              }}
            />
          </div>
        </div>
      </ModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('calendar.calendarsSettings.deleteTitle')}
        description={t('calendar.calendarsSettings.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteCalendar(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('calendar.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
